import api from './api';
import { computed, toValue } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { QueryGcTime } from '@/constants/http';
import { useSearchAwareParams } from '@/composables/useSearchAwareParams';
import { useToast } from '@/composables/useToast.js';
import { useAgencyUserStore } from '@/stores/agencyUsers.js';

export const useAgencies = (params = {}) => {
  const searchParams = useSearchAwareParams({
    searchTerm: () => params.search_text,
    defaultParams: params,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });

  return useQuery({
    queryKey: ['agencies', searchParams],
    queryFn: () => api.getAgencies(searchParams.value),
  });
};

export const useAgencyById = (id, options = { gcTime: QueryGcTime, enabled: true, retry: 3 }) => {
  return useQuery({
    queryKey: ['agency', id],
    queryFn: async () => await api.getAgencyById(toValue(id)),
    gcTime: options.gcTime ?? QueryGcTime,
    enabled: computed(() => !!toValue(id) && toValue(options?.enabled)),
    retry: options?.retry || 0,
  });
};

export const useCreateAgency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createAgency(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
    },
  });
};

export const useCreateAgencyUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createAgencyUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency-users'] });
    },
  });
};

export const useCreateAgencyUserResetLink = () => {
  return useMutation({
    mutationFn: (data) => api.createAgencyUserResetLink(data),
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ['agency-users'] });
    },
  });
};

export const useUsersByAgencyId = (id) => {
  const { filters } = useAgencyUserStore();
  return useQuery({
    queryKey: ['agency-users', id, filters],
    queryFn: async () => await api.getUsersByAgencyId({ id: id, params: filters }),
  });
};

export const useSendInvitationToAgencyUser = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data) => api.sendInvitation(data),
    onSuccess: () => {
      toast({
        heading: 'Invitation email',
        message: 'Sent successfully',
        color: 'success',
        icon: 'check-circle',
      });
    },
  });
};
